import { CustomEventWithDetail, CustomEventWithoutDetail, html, on, View } from 'rune-ts';
import klass from './SizeGauge.module.scss';
import { ChangeRangeEvent, InputRangeEvent, SliderView } from '@modules/NewMaker/UI/slider/slider.ts';
import { CalculatorSize, Size, SizeRange } from '@modules/NewMaker/UI/sizeGauge/CalcualteSize.ts';
import { cx, cxIf, cxQs } from '../util';
import { toastify } from '../Toast/Toast.ts';
import { Icon } from '../Icon/Icon.ts';

export class ChangeSizeRangeEvent extends CustomEventWithDetail<{ slider_value: number; size: Size }> {}
export class HandleSliderValueChange extends CustomEventWithDetail<{ slider_value: number }> {}
export class FocusInputEvent extends CustomEventWithoutDetail {}
export class BlurInputEvent extends CustomEventWithoutDetail {}

export interface SizeGaugeParams {
  width: number;
  height: number;
  is_mobile: boolean;
  disabled: boolean;
  custom_klass?: string;
  hide_slider: boolean;
  size_range: SizeRange;
  slider_value: number;
}

export class SizeGauge extends View<SizeGaugeParams> {
  private slider?: SliderView;
  private ratio: number;
  private size_range: SizeRange;
  private size: Size;
  private is_mobile: boolean;
  private slider_value: number;

  constructor(data: SizeGaugeParams) {
    super(data);
    const { slider_value, width, height, size_range, is_mobile } = this.data;
    this.is_mobile = is_mobile;
    this.ratio = width / height;
    this.size = { width, height };

    this.size_range = size_range;
    this.slider_value = slider_value;
    this.slider = new SliderView({
      value: this.slider_value,
      disabled: this.data.disabled,
    });
  }

  override template() {
    const { disabled, custom_klass, hide_slider } = this.data;
    const { width, height } = this.size;
    const { min_width, min_height, max_width, max_height } = this.size_range;

    return html` <div class="${cx(klass.sizeGauge, cxIf(disabled, klass.disabled), custom_klass)}">
      <div class="${klass.sizeGaugeinputs}">
        <div class="${klass.sizeGaugeinputstwrapper}">
          <div class="${klass.inputwithunit}">
            <label>${TT('flexi_maker::width')}</label>
            <input
              type="number"
              class="${klass.widthinput}"
              inputmode="numeric"
              pattern="[0-9]*"
              value="${Math.round(width)}"
              min="${min_width}"
              max="${max_width}"
            />
            <span class="${klass.unit}">mm</span>
          </div>
        </div>
        ${new Icon({ name: 'link', size: 12.8, theme: disabled ? 'gray-500' : 'none' })}
        <div class="${klass.sizeGaugeinputstwrapper}">
          <div class="${klass.inputwithunit}">
            <label>${TT('flexi_maker::height')}</label>
            <input
              type="number"
              class="${klass.heightinput}"
              inputmode="numeric"
              pattern="[0-9]*"
              value="${Math.round(height)}"
              min="${min_height}"
              max="${max_height}"
            />
            <span class="${klass.unit}">mm</span>
          </div>
        </div>
      </div>
      ${hide_slider ? '' : this.slider}
    </div>`;
  }

  override onRender() {
    this.addEventListener(InputRangeEvent, (e: CustomEvent) => {
      this.dispatchEvent(HandleSliderValueChange, {
        bubbles: true,
        detail: {
          slider_value: e.detail.slider_value,
        },
      });
    });

    this.addEventListener(ChangeRangeEvent, (e: CustomEvent) => {
      e.stopImmediatePropagation();
      this.dispatchEvent(ChangeSizeRangeEvent, {
        bubbles: true,
        detail: {
          ...e.detail,
        },
      });
    });

    // this.element().addEventListener('focusin', (e: Event) => {
    //   const target = e.target as HTMLElement;
    //   if (target.matches('input[type="number"]')) {
    //     this.dispatchEvent(FocusInputEvent, { bubbles: true });
    //   }
    // });
    //
    // document.addEventListener('click', (e: Event) => {
    //   const target = e.target as HTMLElement;
    //   if (!target.matches('input[type="number"]')) {
    //     this.dispatchEvent(BlurInputEvent, { bubbles: true });
    //   }
    // });
  }

  @on('change', 'input')
  protected _changeInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = parseInt(target.value);
    if (isNaN(value)) return;

    const is_width = target.classList.contains(klass.widthinput);
    if (is_width || target.classList.contains(klass.heightinput)) {
      this.updateSlider(is_width, value);
    }
  }

  private updateSlider(is_width_changed: boolean, value: number) {
    if (isNaN(value) || value <= 0) return;

    // 임시 기댓값 (dispatchEvent 후에 updateDOM())
    const new_size: Size = is_width_changed
      ? {
          width: value,
          height: Math.round(value / this.ratio),
        }
      : {
          width: Math.round(value * this.ratio),
          height: value,
        };

    this.size = new_size;

    const slider_value = CalculatorSize.calculateSliderValue(new_size, this.size_range);

    if (!this.slider) return;

    this.slider.data.value = slider_value;

    const is_valid = this.validateAndAdjustSize(new_size, this.size_range);

    this.dispatchEvent(ChangeSizeRangeEvent, {
      bubbles: true,
      detail: {
        slider_value: this.slider.data.value,
        size: { ...this.size, is_width_changed },
        is_valid,
      },
    });

    this.redraw();
  }

  updateDOM(size: Size) {
    if (!size) return;
    const width_input = this.element().querySelector(cxQs(klass.widthinput)) as HTMLInputElement;
    const height_input = this.element().querySelector(cxQs(klass.heightinput)) as HTMLInputElement;

    if (width_input) width_input.value = Math.round(size.width).toString();
    if (height_input) height_input.value = Math.round(size.height).toString();
    this.size = size;
  }

  private validateAndAdjustSize(size: Size, size_range: SizeRange) {
    const adjust = CalculatorSize.checkOverflow(size, size_range);

    if (!adjust) return true;
    if (!this.slider) return true;
    this.size = adjust.size;
    this.slider.data.value = adjust.slider_value;
    return false;

    // this.showSizeAdjustedToast();
  }

  public showSizeAdjustedToast() {
    const flexi_board_el = document.querySelector('.flexi-maker-board') as HTMLElement;
    const board_el = document.querySelector('.board') as HTMLElement;
    const top = flexi_board_el.getBoundingClientRect().top - board_el.getBoundingClientRect().top;

    toastify({
      text: TT('flexi_maker::toast::available'),
      position: 'top-center',
      time: 2000,
      parent: (document.body?.querySelector('.board') as HTMLElement) || document.body,
      single: true,
      offset: {
        x: 0,
        y: top <= 0 ? 0 : top - 36 - 40,
      },
      ...(this.is_mobile && {
        offset: {
          x: 0,
          y: 80,
        },
      }),
    });
  }

  setDisable(bool: boolean) {
    this.data.disabled = bool;
    this.redraw();
  }
}
